import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
} from '@angular/core';
import {
  OrganisationService,
  StafferSettingsStoreService,
} from '@principle-theorem/ng-principle-shared';
import {
  LabeledInputOption,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  DEFAULT_TIMELINE_DATE_FORMAT,
  PRACTICE_TIMELINE_TOOLTIP_DELAY_OPTIONS,
  TIMELINE_DISPLAY_ZOOM_LABELS,
  TimelineDateFormat,
  TimelineDisplayOrientation,
  TimelineDisplayZoom,
  type ITimelineStafferDisplayOptions,
  type PracticeTimelineTooltipDelay,
} from '@principle-theorem/principle-core/interfaces';
import { getEnumValues, isChanged$ } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pr-timeline-user-settings',
  templateUrl: './timeline-user-settings.component.html',
  styleUrls: ['./timeline-user-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimelineUserSettingsComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  stepSizesInMins: number[] = [5, 10, 15, 20];
  tooltipDelays: PracticeTimelineTooltipDelay[] =
    PRACTICE_TIMELINE_TOOLTIP_DELAY_OPTIONS;
  timelineOrientations = getEnumValues(TimelineDisplayOrientation);
  zoomOptions = getEnumValues(TimelineDisplayZoom).map(
    (zoom): LabeledInputOption => ({
      label: TIMELINE_DISPLAY_ZOOM_LABELS[zoom],
      value: zoom,
    })
  );
  dateFormatOptions = getEnumValues(TimelineDateFormat).map((format) => ({
    label: moment().format(format),
    value: format,
  }));

  stafferViewSettingsForm = new TypedFormGroup<ITimelineStafferDisplayOptions>({
    orientation: new TypedFormControl<TimelineDisplayOrientation>(),
    zoom: new TypedFormControl<TimelineDisplayZoom>(),
    dateFormat: new TypedFormControl<TimelineDateFormat>(
      DEFAULT_TIMELINE_DATE_FORMAT
    ),
    thickTracks: new TypedFormControl<boolean>(),
    showGridlines: new TypedFormControl<boolean>(),
    hideEmptyDays: new TypedFormControl<boolean>(),
    hideCancelledAppointments: new TypedFormControl<boolean>(),
  });

  constructor(
    private _organisation: OrganisationService,
    public timelineStore: StafferSettingsStoreService
  ) {
    this._organisation.stafferSettings$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((settings) => {
        if (!settings?.timeline) {
          return;
        }
        this.stafferViewSettingsForm.patchValue(settings.timeline, {
          emitEvent: false,
        });
      });

    this.stafferViewSettingsForm.valueChanges
      .pipe(isChanged$(), takeUntil(this._onDestroy$))
      .subscribe((settings) =>
        this.timelineStore.updateStafferSettings({
          timeline: settings,
        })
      );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  selectDateFormat(format: TimelineDateFormat): void {
    this.stafferViewSettingsForm.patchValue({
      dateFormat: format,
    });
  }
}
